<template>
  <div
    class="login-page-wrapper"
    :style="'background-color:' + (backgroundColor1 || '#f5f5f5')">
    <v-container
      class="login-container"
      :style="'background-color:' + (backgroundColor1 || '#f5f5f5')">
      
      <!-- Step 1: Email Input -->
    <div v-if="!state.requested_token" class="login-step">
      <!-- Logo -->
      <div class="logo-container">
        <img :src="brandingImg" class="brand-logo" alt="Logo">
      </div>

      <!-- Title -->
      <h1 class="login-title">Log in</h1>
      <p class="login-subtitle">
        {{ state.inputMode === 'email' ? 'Enter your email address' : 'Enter your phone number' }}
      </p>

      <!-- Clinic Selection (if multiple clinics) -->
      <div v-if="state.clinics.length > 1" class="form-group">
        <label class="input-label">Select Clinic</label>
        <v-select
          :items="state.clinics"
          v-model="state.clinic"
          variant="outlined"
          density="comfortable"
          hide-details
          class="clinic-select"
        ></v-select>
      </div>

      <!-- Email Input -->
      <div v-if="state.inputMode === 'email'" class="form-group">
        <label class="input-label">Your email address</label>
        <v-text-field
          v-model="state.email"
          type="email"
          variant="outlined"
          density="comfortable"
          placeholder="Enter your email"
          hide-details
          :error="!!state.error"
          class="email-input"
          @keyup.enter="requestToken('email')"
        ></v-text-field>
      </div>

      <!-- Phone Input -->
      <div v-if="state.inputMode === 'phone'" class="form-group">
        <label class="input-label">Your phone number</label>
        <v-text-field
          v-model="state.phone"
          type="tel"
          variant="outlined"
          density="comfortable"
          placeholder="Enter your phone number"
          hide-details
          :error="!!state.error"
          class="phone-input"
          @keyup.enter="requestToken('sms')"
        ></v-text-field>
      </div>

      <!-- Error Message -->
      <div v-if="state.error && !state.requested_token" class="error-message">
        {{ state.error }}
      </div>

      <!-- Continue Button -->
      <v-btn
        :color="buttonColor || '#7c3aed'"
        block
        size="large"
        class="continue-button"
        :loading="state.loading"
        :disabled="(!state.email && !state.phone) || state.loading"
        @click="requestToken(state.inputMode === 'email' ? 'email' : 'sms')"
      >
        Continue
      </v-btn>

      <!-- Toggle Input Mode -->
      <!--v-btn
        variant="outlined"
        block
        size="large"
        class="sms-button"
        :disabled="state.loading"
        @click="toggleInputMode"
      >
        {{ state.inputMode === 'email' ? 'Log in with phone number' : 'Log in with email' }}
      </v-btn-->

      <!-- App Store Links -->
      <div class="app-links">
        <p class="app-links-text">Best experience? It's in the app</p>
        <div class="store-badges">
          <a href="https://apps.apple.com/gb/app/swandoola-3-0-clients/id6746348440">
            <img
              src="/img/app-store.png"
              alt="App Store"
              class="store-badge"
            />
          </a>
          <a href="https://play.google.com/store/apps/details?id=swandoola.com&hl=en_GB&pli=1">
            <img
              src="/img/android.png"
              alt="Google Play"
              class="store-badge"
            />
          </a>
        </div>
      </div>
    </div>

    <!-- Step 2: Token Input -->
    <div v-else class="login-step">
      <!-- Help Icon -->
      <v-btn
        icon
        variant="text"
        class="help-button"
        @click="showHelp = !showHelp"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 16v.01M12 12c0-1.1.9-2 2-2s2-.9 2-2-.9-2-2-2-2 .9-2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </v-btn>

      <!-- Title -->
      <h1 class="login-title">Enter your code</h1>
      <p class="login-subtitle">
        We just sent a verification code to your {{ state.loginMethod === 'sms' ? 'phone' : 'email' }}
        <strong>{{ state.loginMethod === 'sms' ? state.phone : state.email }}</strong>. It may take a few moments to arrive
      </p>

      <!-- Token Input -->
      <div class="form-group">
        <v-text-field
          v-model="state.token"
          variant="outlined"
          density="comfortable"
          placeholder="Enter code"
          hide-details
          :error="!!state.error"
          class="token-input"
          autofocus
          @keyup.enter="submitToken"
        ></v-text-field>
      </div>

      <!-- Error Message -->
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <!-- Resend Timer -->
      <div class="resend-section">
        <span v-if="state.resendTimer > 0" class="resend-text">
          Resend in {{ formatTime(state.resendTimer) }}
        </span>
        <v-btn
          v-else
          variant="text"
          class="resend-button"
          @click="resendCode"
        >
          Resend code
        </v-btn>
      </div>

      <!-- Help Text -->
      <div v-if="showHelp" class="help-text">
        <p><strong>Didn't receive the code?</strong></p>
        <ul>
          <li v-if="state.loginMethod === 'email'">Check your spam/junk folder</li>
          <li>Make sure {{ state.loginMethod === 'sms' ? state.phone : state.email }} is correct</li>
          <li>Wait a few moments for the code to arrive</li>
          <li>Try resending the code</li>
        </ul>
      </div>

      <!-- Auto Submit on Complete -->
      <v-btn
        :color="buttonColor || '#7c3aed'"
        block
        size="large"
        class="continue-button"
        :loading="state.loading"
        :disabled="!state.token || state.loading"
        @click="submitToken"
      >
        Verify Code
      </v-btn>
    </div>
    </v-container>
  </div>
</template>

<script lang="ts" setup>
import { reactive, computed, ref, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { loginService } from "@/api/services/login";
import { useSettingsStore } from '@/api/store/settings';
import { useUserStore } from '@/api/store/user';
import { setToken, setUser } from "@/api/services/auth"
import { appService } from '@/api/services/app'

const settingsStore = useSettingsStore();
const userStore = useUserStore();
const router = useRouter();

// Computed property to force reactivity
const buttonColor = computed(() => settingsStore.settings.buttonColor);
const backgroundColor1 = computed(() => settingsStore.settings.backgroundColor1);
const brandingImg = computed(() => settingsStore.settings.brandingImg);

const showHelp = ref(false);
let resendInterval: any = null;

const state = reactive({
  email: "",
  phone: "",
  token: "",
  requested_token: false,
  loading: false,
  error: "",
  clinic: null,
  clinics: [],
  loginMethod: 'email' as 'email' | 'sms',
  inputMode: 'email' as 'email' | 'phone',
  resendTimer: 0
});

// Filter technical errors to show user-friendly messages
const getUserFriendlyError = (error: any, defaultMessage: string): string => {
  // Technical error patterns that should never be shown to users
  const technicalPatterns = [
    'http',
    'undefined',
    'null',
    'Cannot read properties',
    'Cannot access',
    'is not defined',
    'stream_socket',
    'socket_client',
    'Connection refused',
    'Connection could not be established',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'Network Error',
    '127.0.0.1',
    'localhost',
    'at eval',
    'at Object',
    'at Array',
    'at Proxy',
    '.vue',
    '.js',
    '.ts',
    'scriptLoader',
    'node_modules',
    'webpack',
    'TypeError',
    'ReferenceError',
    'SyntaxError',
    'at line',
    'stack trace',
    'Exception',
    'Fatal error',
    'host ',
    'port ',
    'Unable to connect',
    'SSL',
    'certificate',
    'authentication failed',
    'query failed',
    'database',
    'SQL',
    'PDO',
    'mysqli'
  ];

  // Check if error message contains any technical patterns
  const isTechnicalError = (message: string): boolean => {
    if (!message || typeof message !== 'string') return true;
    const lowerMessage = message.toLowerCase();
    return technicalPatterns.some(pattern => 
      lowerMessage.includes(pattern.toLowerCase())
    );
  };

  // Try to extract a user-friendly message from the error
  if (error.response?.data?.message && typeof error.response.data.message === 'string') {
    if (!isTechnicalError(error.response.data.message)) {
      return error.response.data.message;
    }
  }

  if (error.message && typeof error.message === 'string') {
    if (!isTechnicalError(error.message)) {
      return error.message;
    }
  }

  // Default to user-friendly message
  return defaultMessage;
};

const getSettings = async () => {
  try {
    const result = await appService.fetchDisplay();
    const data = JSON.parse(result.app.settings);
    
    // Handle branding image if present
    if (result.app?.image?.file?.file_path) {
      data.brandingImg = result.app.image.file.file_path;
    }
    
    // Use store action to update settings
    settingsStore.updateSettings(data);
    
    router.replace({ path: '/' });
    
  } catch (error) {
    console.error('Error in getSettings:', error);
    // On error, just navigate normally
    router.replace({ path: '/' });
  }
}

const toggleInputMode = () => {
  state.inputMode = state.inputMode === 'email' ? 'phone' : 'email';
  state.error = "";
};

const requestToken = async (method: 'email' | 'sms') => {
  // Validate appropriate field
  if (method === 'email' && !state.email) {
    state.error = "Please enter your email address";
    return;
  }
  
  if (method === 'sms' && !state.phone) {
    state.error = "Please enter your phone number";
    return;
  }
  
  state.loading = true;
  state.error = "";
  state.loginMethod = method;

  const serviceCall = method === 'sms' ? loginService.login_sms.bind(loginService) : loginService.login.bind(loginService);

  try {
    const result = await serviceCall({
      email: method === 'email' ? state.email : state.phone,
      otp: state.token,
      requester: "clients-portal",
      clinic_id: state.clinic
    });

    if (result.logins) {
      state.clinics = [];
      result.logins.forEach((login: any) => {
        const clinicName = login.clinic?.name || login.clinic_name || `Clinic ${login.clinic_id}`;
        state.clinics.push({ value: login.clinic_id, title: clinicName });
      });
      if (state.clinics.length > 0) {
        state.clinic = state.clinics[0].value;
      }
      state.error = "";
      // Stay on step 1 to show clinic selection
    } else if (result.error) {
      // Filter error messages from API responses
      state.error = getUserFriendlyError({ message: result.error }, "Unable to send verification code. Please try again.");
    } else {
      state.requested_token = true;
      state.error = "";
      startResendTimer();
    }
  } catch (error: any) {
    console.error('Login request error:', error);
    
    // Determine user-friendly error message based on status
    let userMessage = "Unable to send verification code. Please check your email and try again.";
    
    if (error.response?.status === 404) {
      userMessage = "Email not found. Please check your email address.";
    } else if (error.response?.status === 429) {
      userMessage = "Too many attempts. Please try again later.";
    } else if (error.response?.status >= 500) {
      userMessage = "Server error. Please try again later.";
    }
    
    // Filter out any technical errors and use user-friendly message
    state.error = getUserFriendlyError(error, userMessage);
  } finally {
    state.loading = false;
  }
};

const submitToken = async () => {
  if (!state.token) {
    state.error = "Please enter your verification code";
    return;
  }

  state.loading = true;
  state.error = "";

  try {
    const result = await loginService.login_otp.bind(loginService)({
      email: state.loginMethod === 'email' ? state.email : state.phone,
      otp: state.token,
      requester: "clients-portal",
      clinic_id: state.clinic
    });

    if (result.success) {
      setToken(result.token.access_token);
      setUser(result.user);
      userStore.logged_in = true;
      userStore.user = result.user;
      await getSettings();
    } else {
      // Filter result messages as well to ensure no technical errors leak through
      const message = result.message || "Invalid code. Please try again.";
      state.error = getUserFriendlyError({ message }, "Invalid code. Please try again.");
    }
  } catch (error: any) {
    console.error('Token verification error:', error);
    
    // Determine user-friendly error message based on status
    let userMessage = "Unable to verify code. Please try again.";
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      userMessage = "Invalid or expired code. Please try again.";
    } else if (error.response?.status === 429) {
      userMessage = "Too many attempts. Please try again later.";
    } else if (error.response?.status >= 500) {
      userMessage = "Server error. Please try again later.";
    }
    
    // Filter out any technical errors and use user-friendly message
    state.error = getUserFriendlyError(error, userMessage);
  } finally {
    state.loading = false;
  }
};

const goBack = () => {
  state.requested_token = false;
  state.token = "";
  state.error = "";
  clearResendTimer();
};

const resendCode = () => {
  state.token = "";
  state.error = "";
  // Ensure we resend using the same method
  requestToken(state.loginMethod);
};

const startResendTimer = () => {
  state.resendTimer = 60;
  resendInterval = setInterval(() => {
    state.resendTimer--;
    if (state.resendTimer <= 0) {
      clearResendTimer();
    }
  }, 1000);
};

const clearResendTimer = () => {
  if (resendInterval) {
    clearInterval(resendInterval);
    resendInterval = null;
  }
  state.resendTimer = 0;
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

onUnmounted(() => {
  clearResendTimer();
});
</script>

<style scoped>
.login-page-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile browsers */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.login-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px 16px;
  min-height: 100vh;
  min-height: 100dvh; /* Dynamic viewport height for mobile browsers */
}

.login-step {
  max-width: 420px;
  width: 100%;
  padding: 0;
  position: relative;
}

.help-button {
  position: absolute;
  top: -10px;
  right: -10px;
  color: #7c3aed;
}

.logo-container {
  display: flex;
  justify-content: center;
  margin: 20px 0 24px;
}

.brand-logo {
  max-width: 160px;
  max-height: 60px;
  object-fit: contain;
}

.login-title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
  line-height: 1.2;
}

.login-subtitle {
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.5;
}

.login-subtitle strong {
  color: #1a1a1a;
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.email-input,
.phone-input,
.token-input,
.clinic-select {
  font-size: 16px;
}

.email-input :deep(.v-field),
.phone-input :deep(.v-field),
.token-input :deep(.v-field),
.clinic-select :deep(.v-field) {
  border-radius: 12px;
  background-color: white;
}

.error-message {
  color: #ef4444;
  font-size: 14px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background-color: #fef2f2;
  border-radius: 8px;
  border-left: 3px solid #ef4444;
}

.continue-button {
  border-radius: 12px;
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  margin-bottom: 10px;
  height: 52px !important;
}

.sms-button {
  border-radius: 12px;
  text-transform: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  margin-bottom: 20px;
  height: 52px !important;
  color: #666;
  border-color: #e0e0e0;
}

.app-links {
  text-align: center;
  margin-top: 24px;
}

.app-links-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
}

.store-badges {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.store-badge {
  width: 90px;
  height: auto;
}

.resend-section {
  text-align: center;
  margin: 16px 0;
}

.resend-text {
  font-size: 14px;
  color: #666;
}

.resend-button {
  text-transform: none;
  font-size: 14px;
  font-weight: 600;
  color: #7c3aed;
}

.help-text {
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #666;
}

.help-text p {
  margin: 0 0 8px 0;
  color: #1a1a1a;
  font-size: 14px;
}

.help-text ul {
  margin: 0;
  padding-left: 18px;
}

.help-text li {
  margin-bottom: 6px;
  line-height: 1.4;
}

.help-text li:last-child {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .login-title {
    font-size: 28px;
  }

  .logo-container {
    margin: 16px 0 20px;
  }
  
  .brand-logo {
    max-width: 140px;
    max-height: 50px;
  }
}
</style>
